import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "./api";

export type Admin = {
  id: string;
  email: string;
  name: string;
  status: string;
  last_login_at?: string | null;
  created_at: string;
  updated_at: string;
  // Normalised PROPWISE_ENV of the adminserver this session is bound to
  // ("prod", "staging", …). Drives the environment badge in AdminShell.
  // Optional so an older backend that omits it degrades gracefully.
  env?: string;
};

type AuthState = {
  admin: Admin | null;
  pending: boolean;
  refresh: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [pending, setPending] = useState(true);

  const refresh = async () => {
    try {
      const me = await api.get<Admin>("/admin/auth/me");
      setAdmin(me);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setAdmin(null);
      } else {
        // Network or 500 — leave previous state so a transient blip
        // doesn't kick the user out.
      }
    } finally {
      setPending(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const signIn = async (email: string, password: string) => {
    const me = await api.post<Admin>("/admin/auth/login", { email, password });
    setAdmin(me);
  };

  const signOut = async () => {
    try { await api.post("/admin/auth/logout"); } catch { /* ignore */ }
    setAdmin(null);
  };

  return <Ctx.Provider value={{ admin, pending, refresh, signIn, signOut }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth used outside AuthProvider");
  return v;
}
